import type { IShipmentContainersFilters, IShipmentFilters } from '@/types/rc/filters'
import type { IShipmentFormInbound, IShipmentFormOutbound, IShipmentFormTrackingInformation } from '@/types/rc/forms'
import type { IDashboardShipment, IShipment, IShipmentContainer } from '@/types/rc/shipments'
import type { IPage } from '@/types/shared/pagination'
import axios from 'axios'

/**********************/
/**** Core queries ****/
/**********************/

/**
 * Gets all shipments.
 *
 * @param {any} params
 * @returns {Promise<IPage<IDashboardShipment>>} a page with shipments.
 * @throws {AxiosError}
 */

export const getAllShipments = async (params: IShipmentFilters): Promise<IPage<IDashboardShipment>> => {
  const res = await axios.get(import.meta.env.VITE_APP_RC_SHIPMENTS_ENDPOINT, {
    params,
  })

  return res.data
}

/**
 * Gets a shipment by ID.
 *
 * @param {number} shipmentId
 * @returns {Promise<any>} a shipment object.
 * @throws {AxiosError}
 */

export const getShipmentDetails = async (shipmentId: number): Promise<IShipment> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_SHIPMENTS_ENDPOINT}/${shipmentId}`)
  return res.data
}

/************************/
/**** Core mutations ****/
/************************/

/**
 * Creates a new inbound shipment.
 *
 * @param {IShipmentFormInbound} createInboundShipmentForm an inbound shipment form.
 * @returns newly-created inbound shipment object.
 * @throws {AxiosError}
 */
export async function createInboundShipment(createInboundShipmentForm: IShipmentFormInbound) {
  const createInboundFormData = new FormData()

  createInboundFormData.append('work_order_number', createInboundShipmentForm.work_order!.number)

  if (createInboundShipmentForm.customer_drop_off)
    createInboundFormData.append('customer_drop_off', String(createInboundShipmentForm.customer_drop_off))

  setCommonShipmentFormData(createInboundShipmentForm, createInboundFormData)

  const res = await axios.post(import.meta.env.VITE_APP_RC_SHIPMENTS_INBOUND_ENDPOINT, createInboundFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Updates an inbound shipment.
 *
 * @param inboundForm the updated inbound shipment form.
 * @returns the updated inbound shipment.
 */
export async function updateInboundShipment(inboundForm: IShipmentFormInbound) {
  const inboundFormData = new FormData()

  inboundFormData.append('shipment_id', inboundForm.id!)
  if (inboundForm.customer_drop_off) inboundFormData.append('customer_drop_off', String(inboundForm.customer_drop_off))

  setCommonShipmentFormData(inboundForm, inboundFormData)

  if (inboundForm.deleted_receipt_ids) {
    inboundForm.deleted_receipt_ids.forEach((id) => {
      inboundFormData.append('deleted_receipt_ids', id.toString())
    })
  }

  const res = await axios.put(import.meta.env.VITE_APP_RC_SHIPMENTS_INBOUND_ENDPOINT, inboundFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Creates an outbound shipment.
 *
 * @param createOutboundShipmentForm outbound shipment form.
 * @returns newly-created outbound shipment object.
 * @throws {AxiosError}
 */
export async function createOutboundShipment(createOutboundShipmentForm: IShipmentFormOutbound) {
  const createOutboundFormData = new FormData()

  createOutboundFormData.append('work_order_number', createOutboundShipmentForm.work_order!.number)
  if (createOutboundShipmentForm.picked_up_date)
    createOutboundFormData.append('picked_up_date', createOutboundShipmentForm.picked_up_date)
  if (createOutboundShipmentForm.customer_pick_up)
    createOutboundFormData.append('customer_pick_up', String(createOutboundShipmentForm.customer_pick_up))

  setCommonShipmentFormData(createOutboundShipmentForm, createOutboundFormData)

  const res = await axios.post(import.meta.env.VITE_APP_RC_SHIPMENTS_OUTBOUND_ENDPOINT, createOutboundFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Updates an outbound shipment.
 *
 * @param outboundForm the updated outbound shipment.
 * @returns the updated outbound shipment.
 */
export async function updateOutboundShipment(outboundForm: IShipmentFormOutbound) {
  const outboundFormData = new FormData()

  outboundFormData.append('shipment_id', outboundForm.id!)
  setCommonShipmentFormData(outboundForm, outboundFormData)
  if (outboundForm.picked_up_date) outboundFormData.append('picked_up_date', outboundForm.picked_up_date)
  if (outboundForm.customer_pick_up) outboundFormData.append('customer_pick_up', String(outboundForm.customer_pick_up))

  if (outboundForm.deleted_receipt_ids) {
    outboundForm.deleted_receipt_ids.forEach((id) => {
      outboundFormData.append('deleted_receipt_ids', id.toString())
    })
  }

  const res = await axios.put(import.meta.env.VITE_APP_RC_SHIPMENTS_OUTBOUND_ENDPOINT, outboundFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

/**
 * Sets common shipment form data.
 *
 * @param form an inbound or outbound shipment form.
 * @param formData inbound or outbound multipart/form-data.
 */
function setCommonShipmentFormData(form: IShipmentFormInbound | IShipmentFormOutbound, formData: FormData) {
  // Tracking Information
  // Allow `null`
  if (form.tracking_information.carrier !== undefined) {
    for (const key in form.tracking_information) {
      if (form.tracking_information[key as keyof IShipmentFormTrackingInformation] !== undefined)
        formData.append(
          `tracking_information.${key}`,
          (form.tracking_information[key as keyof IShipmentFormTrackingInformation] as string | Blob) ?? ''
        )
    }
  }

  // Expected Content
  form.expected_container_types?.forEach((type) => {
    formData.append('expected_container_types', type)
  })

  if (form.expected_containers) {
    formData.append('expected_containers', form.expected_containers.toString().replaceAll(',', ''))
  }

  if (form.expected_miners) {
    formData.append('expected_miners', form.expected_miners.toString().replaceAll(',', ''))
  }

  // Receipts
  if (form.receipts) {
    for (const receipt of form.receipts) {
      if (receipt?.raw) formData.append('receipts', receipt.raw)
    }
  }

  // Picked Up Date
  if (form.picked_up_date) formData.append('picked_up_date', form.picked_up_date)

  // Delivered Date
  if (form.delivered_date) formData.append('delivered_date', form.delivered_date)
}

/**
 * Delete a shipment by shipment ID.
 *
 * @param shipmentId the shipment's ID
 */
export async function deleteShipment(shipmentId: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_SHIPMENTS_ENDPOINT}/${shipmentId}`)
  return res.data
}

/*****************************/
/**** Containers-related *****/
/*****************************/

/**
 * Gets shipment containers.
 * @param {number} shipmentId
 * @param {IFilters} filters
 * @returns {Promise<any>} an array of shipment containers.
 * @throws {AxiosError}
 */

export const getShipmentContainers = async (
  shipmentId: number,
  filters: IShipmentContainersFilters
): Promise<IPage<IShipmentContainer>> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_SHIPMENTS_ENDPOINT}/${shipmentId}/containers`, {
    params: {
      ...filters,
    },
  })
  return res.data
}

/**
 * Assign containers to shipment
 *
 * @param id shipment ID
 * @param containerIDs an array with all the container IDs to assign
 */
export async function assignContainersToShipment(id: number, containerIDs: number[]) {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_SHIPMENTS_ENDPOINT}/${id}/containers`, {
    container_ids: containerIDs,
  })
  return res.data
}

/**
 * Unassign container from shipment
 *
 * @param id shipment ID
 * @param containerNumber container number to unassign
 */
export async function unassignContainerFromShipment(id: string, containerID: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_SHIPMENTS_ENDPOINT}/${id}/containers/${containerID}`)
  return res.data
}

/**************/
/**** Misc ****/
/**************/

/**
 * Gets all carriers.
 *
 * @returns {Promise<any>} an array of carriers.
 * @throws {AxiosError}
 */

export const getAllCarriers = async (): Promise<string[]> => {
  const res = await axios.get(import.meta.env.VITE_APP_RC_CARRIERS_ENDPOINT)
  return res.data
}
